import React from 'react'
import tw from 'twin.macro'
import { Tooltip } from '@mui/material'
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { RowActionsMenu } from 'src/components/shared/grid/RowActionsMenu'
import { RefundStatus } from 'src/constants/payments-ops/enums'
import { refundReferenceTypeLabels } from 'src/constants/payments-ops/labels'
import type { Refund } from 'src/interfaces/refund'
import { formatDateTime, parseFlexibleDateTime, parseUnixTimestamp } from 'src/utils/grid'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { maskAccountNumber } from 'src/utils/paymentsOps/maskAccountNumber'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'

interface RefundColumnOptions {
  status: RefundStatus
  nameMap: Record<string, string>
  /** False without `refunds_execute`: Execute is the only row action, so the column is dropped. */
  canExecute: boolean
  onExecute(refund: Refund): void
  /** Non-null disables Execute on that row with this tooltip (e.g. the killswitch stops it). */
  getExecuteDisabledReason(refund: Refund): string | null
}

const TIMESTAMP_FIELD_BY_STATUS: Partial<Record<RefundStatus, keyof Refund>> = {
  [RefundStatus.InProgress]: 'processedAt',
  [RefundStatus.Paid]: 'paidAt',
  [RefundStatus.Failed]: 'failedAt',
}

const TIMESTAMP_LABEL_BY_STATUS: Partial<Record<RefundStatus, string>> = {
  [RefundStatus.InProgress]: 'Processed at',
  [RefundStatus.Paid]: 'Paid at',
  [RefundStatus.Failed]: 'Failed at',
}

/** Execute is offered only on Created and Failed — the backend 400s on any other status. */
const isExecutable = (status: RefundStatus): boolean => {
  return status === RefundStatus.Created || status === RefundStatus.Failed
}

export const buildRefundColumns = ({
  status,
  nameMap,
  canExecute,
  onExecute,
  getExecuteDisabledReason,
}: RefundColumnOptions): Array<GridColDef<Refund>> => {
  const columns: Array<GridColDef<Refund>> = [
    {
      field: 'createdAt',
      headerName: 'Created at',
      width: 180,
      sortable: false,
      valueGetter: parseUnixTimestamp,
      valueFormatter: formatDateTime,
    },
    {
      field: 'id',
      headerName: 'Refund ID',
      width: 150,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Refund, string>) => {
        return <span css={tw`truncate font-mono text-xs`}>{params.value}</span>
      },
    },
    {
      field: 'partnerId',
      headerName: 'Partner',
      flex: 1,
      minWidth: 160,
      sortable: false,
      valueGetter: (value: string) => {
        return resolvePartnerName(nameMap, value)
      },
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      valueFormatter: (value: number) => {
        return formatCentsAsUsd(value)
      },
    },
    {
      field: 'referenceType',
      headerName: 'Reference',
      flex: 1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<Refund>) => {
        return (
          <div css={tw`flex min-w-0 flex-col`}>
            <span>
              {refundReferenceTypeLabels[params.row.referenceType] ?? params.row.referenceType}
            </span>
            <span css={tw`truncate font-mono text-xs text-shades-500`}>
              {params.row.referenceId}
            </span>
          </div>
        )
      },
    },
    {
      field: 'destinationLast4',
      headerName: 'Destination',
      width: 130,
      sortable: false,
      valueGetter: (value: string | undefined) => {
        return maskAccountNumber(value)
      },
    },
  ]

  const timestampField = TIMESTAMP_FIELD_BY_STATUS[status]

  if (timestampField) {
    columns.push({
      field: timestampField,
      headerName: TIMESTAMP_LABEL_BY_STATUS[status] ?? '',
      width: 180,
      sortable: false,
      valueGetter: parseFlexibleDateTime,
      valueFormatter: formatDateTime,
    })
  }

  if (status === RefundStatus.Failed) {
    columns.push({
      field: 'failureReason',
      headerName: 'Failure reason',
      flex: 1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<Refund, string | undefined>) => {
        return (
          <Tooltip title={params.row.internalFailureReason ?? ''}>
            <span css={tw`truncate text-critical-500`}>{params.value ?? '—'}</span>
          </Tooltip>
        )
      },
    })
  }

  if (isExecutable(status) && canExecute) {
    columns.push({
      field: 'actions',
      headerName: '',
      width: 68,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams<Refund>) => {
        const executeDisabledReason = getExecuteDisabledReason(params.row)

        return (
          <RowActionsMenu
            ariaLabel="Refund actions"
            dataTestId={`refund-actions-${params.row.id}`}
            items={[
              {
                label: 'Execute refund',
                disabled: executeDisabledReason !== null,
                disabledReason: executeDisabledReason ?? undefined,
                onClick: () => {
                  onExecute(params.row)
                },
              },
            ]}
          />
        )
      },
    })
  }

  return columns
}
